import {
  Accordion,
  AccordionControl,
  AccordionItem,
  AccordionPanel,
  Anchor,
  Button,
  Checkbox,
  CloseButton,
  Divider,
  Group,
  Image,
  LoadingOverlay,
  NumberInput,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import {
  createLinkedinAccount,
  editLinkedinAccount,
  getLinkedinAccounts,
} from "../../../../../api/EmailAPI";
import { useMemo, useState } from "react";
import { notifications } from "@mantine/notifications";
import { useLanguage } from "../../../../../stores/LanguageStore";
import LinkedinCookie from "./LinkedinCookie";
import BackButton from "../../../../../components/View/BackButton";
import {
  IconCookie,
  IconDeviceFloppy,
  IconExternalLink,
  IconPlus,
  IconPuzzle,
  IconRocket,
  IconSettings,
} from "@tabler/icons-react";
import Sparkles from "../../../../../components/Sparkles/Sparkles";
import useNavigationBlocker from "../../../../../components/View/NavigationBlocker";
import { useForm } from "react-hook-form";
import { SharingInAccordion } from "../../../../../components/Sharing/SharingController";
import PoolsSelector from "../../../../../components/Selectors/PoolsSelector";
import { useNavigate, useRouteLoaderData } from "react-router-dom";
import {
  useProfileStore,
  useSessionStore,
} from "../../../../../stores/UserStore";
import { modals } from "@mantine/modals";
import {
  useAccountsStore,
  useNotificationStore,
} from "../../../../../stores/ConfigStore";
import StepLoader from "../../../../../components/View/StepLoader";
import LinkedinError from "./LinkedinError";
import VideoModalViewer from "../../../../../components/View/VideoModalViewer";
import { PlayIcon } from "../../../../../components/Icons/SvgIcons";
import { getYouTubeMaxThumbnail } from "../../../../../functions/utilFunctions";

const LinkedinAccount = ({
  onCreated,
  handleClose,
  isPage = false,
  accountProfile,
  inList,
  actualPool,
  defaultHeight = 0
}) => {
  const lang = useLanguage((s) => s.language);
  const reseller = useSessionStore((s) => s.reseller);
  const [loading, setLoading] = useState(false);
  const [purge, setPurge] = useState(
    !!accountProfile?.withdraw_pending_invitations
  );
  const [dirty, setDirty] = useState(false);
  const [pools, setPools] = useState(
    accountProfile?.pools
      ? accountProfile.pools
      : actualPool
        ? [actualPool]
        : []
  );
  const sharingForm = useForm({
    defaultValues: {
      sharing:
        accountProfile?.sharing?.map((x) => ({
          ...x,
          sharing_targets: x.sharing_targets.map((sharing_target) => ({
            ...sharing_target,
            option_name: sharing_target.name,
          })),
        })) || [],
    },
  });
  const resellerVideo = reseller?.videos?.find(
    (x) => x.name === "IntegrationLinkedinConnectTutorial"
  );

  const navigate = useNavigate();

  const handleAccountProcessed = (actual) => {
    setLoading(false);

    // If we still don't have an account after polling, stop here.
    if (!actual) return;

    if (!actual.is_valid && actual?.username) {
      navigate("/senders/linkedin/" + actual.id);
      modals.open({
        modalId: "account-invalid",
        title: "Compte non vérifié",
        centered: true,
        size: "lg",
        children: <LinkedinError />,
      });
    } else {
      if (actual.checkpoint_required) {
        const needVerifIgnores =
          useNotificationStore.getState().needVerifIgnores;
        const setNeedVerifIgnores =
          useNotificationStore.getState().setNeedVerifIgnores;
        const setAccountVerif = useAccountsStore.getState().setAccountVerif;

        setNeedVerifIgnores(needVerifIgnores.filter((x) => x !== actual.id));

        setAccountVerif({ ...actual });
      }
      notifications.show({
        message: accountProfile
          ? lang.linkedin_accounts.row_highlight.updated
          : lang.linkedin_accounts.row_highlight.created,
        color: "tertiary.8",
      });
      setTimeout(() => {
        onCreated?.(actual.id);
        handleClose?.();
      }, 100);
    }
  };

  const pollForAccount = async (targetId) => {
    const startTime = Date.now();
    // Poll for up to 60 seconds
    while (Date.now() - startTime < 60000) {
      try {
        const res = await getLinkedinAccounts();
        const acc = res.data.linkedin_accounts_list?.find((x) => x.id === targetId);

        // Stop if account is found AND validity_tested is true
        if (acc && acc.validity_tested) return acc;
      } catch (e) {
        console.error(e);
      }
      // Wait 2s before retry
      await new Promise((r) => setTimeout(r, 2000));
    }
    // One final attempt or return whatever we found
    const finalRes = await getLinkedinAccounts();
    return finalRes.data.linkedin_accounts_list?.find((x) => x.id === targetId);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let fd = new FormData(e.target);
    let data = Object.fromEntries(fd);

    data.is_sales_navigator_account = data.is_sales_navigator_account
      ? true
      : false;
    data.accept_received_invitations = data.accept_received_invitations
      ? true
      : false;
    data.withdraw_pending_invitations = data.withdraw_pending_invitations
      ? true
      : false;
    data.withdraw_pending_invitations_number =
      data.withdraw_pending_invitations_number
        ? parseInt(data.withdraw_pending_invitations_number)
        : 100;
    data.max_conversations_messages_extractor =
      data.max_conversations_messages_extractor
        ? parseInt(data.max_conversations_messages_extractor)
        : 100;
    data.withdraw_pending_invitations_oldest =
      data.withdraw_pending_invitations_oldest ? true : false;
    data.signature = "";
    data.pools_ids = pools?.map((x) => x.id) || [];
    data.user_agent = data?.user_agent || null;
    data.password = data?.password || null;

    const sharing = isPage
      ? sharingForm
        .getValues()
        .sharing.filter((x) => x.sharing_targets.length > 0)
        .map((share) => ({
          ...share,
          ids_list: share.sharing_targets.map((x) => x.id),
          sharing_targets: undefined,
        }))
      : [];

    setLoading(true);

    const main = () =>
      accountProfile
        ? editLinkedinAccount({
          id: accountProfile.id,
          data,
          sharing: sharing?.length > 0 ? sharing : null,
        })
        : createLinkedinAccount(data, sharing);

    main()
      .then((response) => {
        setLoading(true);
        setDirty(false);

        if (!isPage) {
          getLinkedinAccounts()
            .then((res) => {
              let newItemsList = res.data.linkedin_accounts_list;
              let newItem = newItemsList.find(
                (x) => x.id === response.data.linkedin_account_id
              );
              notifications.show({
                title: lang.senders.linkedin.added,
                message: lang.senders.linkedin.added_text,
                color: "tertiary.8",
              });
              onCreated?.(newItem);
              handleClose?.();
            })
            .finally(() => {
              setLoading(false);
            });
        } else {
          getLinkedinAccounts().then((r) => {
            let actual = r.data.linkedin_accounts_list?.find((x) =>
              accountProfile
                ? x.id === accountProfile.id
                : x.id === response.data.linkedin_account_id
            );
            handleAccountProcessed(actual);
          });
        }
      })
      .catch((err) => {
        // Check if error is 504 (Timeout) AND we are in the context where we need to validate (!isPage else block)
        if (err.response?.status === 504) {

          // We need to figure out which ID to poll for.
          // 1. If Editing: We have the ID in accountProfile.
          // 2. If Creating: We DON'T have the ID (request failed). We must try to find it by username.

          const findTargetId = async () => {
            if (accountProfile) return accountProfile.id;

            // If creating, fetch list and match by username (heuristic recovery)
            const r = await getLinkedinAccounts();
            const match = r.data.linkedin_accounts_list?.find(
              x => x.username === data.username && x.name === data.name
            );
            return match?.id;
          };

          findTargetId().then((targetId) => {
            if (targetId) {
              // ID found, start polling
              pollForAccount(targetId).then((actual) => {
                // Once polling finishes (validity true or timeout), run the UI logic
                handleAccountProcessed(actual);
              });
            } else {
              // If we couldn't find the ID even after a fetch, we assume it failed completely.
              setLoading(false);
              // Optional: Show error notification here
            }
          });

        } else {
          // Standard error handling for non-504 errors
          setLoading(false);
        }
      });
  };

  const loadingSteps = useMemo(() => {
    return [
      {
        label: lang.linkedin_accounts.create.dialog.loading_step1_title,
        description:
          lang.linkedin_accounts.create.dialog.loading_step1_description,
      },
      {
        label: lang.linkedin_accounts.create.dialog.loading_step2_title,
        description:
          lang.linkedin_accounts.create.dialog.loading_step2_description,
      },
      {
        label: lang.linkedin_accounts.create.dialog.loading_step3_title,
        description:
          lang.linkedin_accounts.create.dialog.loading_step3_description,
      },
    ];
  }, [lang]);

  useNavigationBlocker(dirty);

  return (
    <Stack
      w={"100%"}
      flex={1}
      gap={"sm"}
      h={defaultHeight}
      mih={accountProfile ? "100%" : "86%"}
      style={{ overflow: "auto" }}
      id="edit-form"
      component={"form"}
      onSubmit={handleSubmit}
      onChange={() => setDirty(true)}
    >
      {loading && <StepLoader steps={loadingSteps} loading={loading} />}
      {accountProfile && (
        <Title order={4} ta={"center"}>
          {lang.linkedin_accounts.edit.dialog.title} : {accountProfile?.name} #
          {accountProfile?.id}
        </Title>
      )}
      <TextInput
        label={lang.linkedin_accounts.create.dialog.name}
        defaultValue={accountProfile?.name}
        required
        name="name"
        placeholder="John Doe"
        size="md"
      />

      {(!accountProfile?.session_cookie || useProfileStore.getState().profile?.level === "admin") && (
        <Paper p="lg" component={Stack}>
          <Group>
            <IconRocket />
            <Title order={4}>
              {lang.linkedin_accounts.create.dialog.connect_title}
            </Title>
          </Group>
          <Text size="lg">
            {lang.linkedin_accounts.create.dialog.connect_text}
          </Text>
          <TextInput
            label={lang.linkedin_accounts.create.dialog.email_label}
            defaultValue={accountProfile?.username}
            name="username"
            placeholder={lang.linkedin_accounts.create.dialog.email_placeholder}
            type="email"
            size="md"
            autoComplete="username-new"
            styles={{
              input: {
                background: "#fff",
              },
            }}
          />

          <PasswordInput
            label={lang.linkedin_accounts.create.dialog.password_label}
            defaultValue={accountProfile?.password}
            name="password"
            placeholder={
              lang.linkedin_accounts.create.dialog.password_placeholder
            }
            size="md"
            autoComplete="new-password"
            id="linkedin-password"
            styles={{
              input: {
                background: "#fff",
              },
            }}
          />
        </Paper>
      )}

      {!accountProfile && (
        <Divider
          my={"xs"}
          label={lang.campaign.or}
          styles={{
            label: {
              fontSize: 16,
              fontWeight: 600,
              textTransform: "uppercase",
            },
          }}
        />
      )}

      {(!accountProfile?.username || useProfileStore.getState().profile?.level === "admin") && (
        <Paper p="lg" component={Stack}>
          <Group>
            <IconCookie />
            <Title order={4}>
              {lang.linkedin_accounts.create.dialog.cookies_title}{" "}
            </Title>
          </Group>
          <Text size="lg">{lang.senders.linkedin.extension_text}</Text>
          <Group wrap="nowrap" w={"100%"} align="start">
            <Stack
              flex={
                reseller?.name !== "Magileads" &&
                  !reseller?.link_chrome_extension
                  ? 1
                  : 0.5
              }
            >
              {(reseller?.name === "Magileads" ||
                (reseller?.name !== "Magileads" &&
                  reseller?.link_chrome_extension)) && (
                  <Sparkles style={{ height: 64 }}>
                    <Anchor
                      href={
                        reseller?.name === "Magileads"
                          ? "https://chromewebstore.google.com/detail/magileads-v2/oojfjkpfkaekaifokifpgfaghiokinaf"
                          : reseller?.link_chrome_extension
                      }
                      target="_blank"
                      onClick={() => handleClose()}
                    >
                      <Button
                        rightSection={
                          <IconExternalLink
                            size={32}
                            style={{ marginLeft: 32 }}
                          />
                        }
                        leftSection={<IconPuzzle />}
                        size="lg"
                        w={"100%"}
                        h={64}
                        fz={18}
                      >
                        {lang.senders.linkedin.extenstion_label.replace(
                          "#",
                          reseller?.name
                        )}
                      </Button>
                    </Anchor>
                  </Sparkles>
                )}

              <Accordion
                variant="contained"
                defaultValue={
                  reseller?.name !== "Magileads" &&
                    !reseller?.link_chrome_extension
                    ? "cookies"
                    : undefined
                }
              >
                <AccordionItem value="cookies">
                  <AccordionControl>
                    <Text>
                      {lang.linkedin_accounts.create.dialog.cookies_manual}
                    </Text>
                  </AccordionControl>

                  <AccordionPanel>
                    <Stack>
                      <Textarea
                        name="session_cookie"
                        label={lang.linkedin_accounts.create.dialog.cookie}
                        defaultValue={accountProfile?.session_cookie}
                        placeholder="AQEDAQAQvlsAEoReAA..."
                        rows={4}
                        // required={
                        //   useProfileStore.getState().profile?.level !== "admin"
                        // }
                        size="md"
                      />
                      <Textarea
                        name="session_cookie_sn"
                        label={lang.linkedin_accounts.create.dialog.cookie_sn}
                        defaultValue={accountProfile?.session_cookie_sn}
                        placeholder="AQEDAQAQvlsAEoReAA..."
                        rows={4}
                        size="md"
                      />
                      {/* {reseller?.name === "Magileads" && <LinkedinCookie />} */}
                    </Stack>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </Stack>
            {reseller?.name === "Magileads" ? (
              <Stack align="center" justify="start" flex={1}>
                <iframe
                  width="100%"
                  height="360"
                  src="https://app.supademo.com/embed/cm5198y810x2wpeaqueawl0ow?embed_v=2"
                  title="How to connect LinkedIn Account"
                  allowFullScreen=""
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  style={{
                    borderRadius: 12,
                    border: "none",
                  }}
                />
              </Stack>
            ) : resellerVideo ? (
              <VideoModalViewer
                src={resellerVideo.url}
                opener={(handleOpen) => (
                  <Stack
                    align="center"
                    justify="start"
                    flex={1}
                    onClick={handleOpen}
                    style={{ position: "relative", cursor: "pointer" }}
                  >
                    <PlayIcon
                      variant="light"
                      color="#fcfbff"
                      size={64}
                      iconSize={32}
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 10,
                      }}
                      radius={64}
                      className="play-button"
                    />

                    {resellerVideo?.url.includes("youtube") || resellerVideo?.thumbnail_url ? (
                      <Image
                        src={getYouTubeMaxThumbnail(resellerVideo?.url, "IntegrationLinkedinConnectTutorial")}
                        style={{ borderRadius: 12, height: "100%" }}
                      />
                    ) : (
                      <video
                        src={resellerVideo?.url}
                        className="reseller-video"
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "block",
                        }}
                      />
                    )}
                  </Stack>
                )}
              />
            ) : (
              ""
            )}
          </Group>
        </Paper>
      )}

      {useProfileStore.getState().profile?.level === "admin" && (
        <TextInput
          name="user_agent"
          label={lang.linkedin_accounts.create.dialog.user_agent}
          defaultValue={accountProfile?.user_agent}
          placeholder="Mozilla/5.0 (Windows NT 10.0; Win64; x64)"
          description="It must be the same than the one used when logging in to LinkedIn"
          size="md"
        />
      )}

      <PoolsSelector
        type={"Linkedin"}
        value={pools}
        setValue={setPools}
        size="md"
      />

      <Accordion mt={"md"} mb={"sm"} variant="contained">
        <Accordion.Item value={"advanced"}>
          <Accordion.Control
            bg={"var(--mantine-color-gray-0)"}
            icon={<IconSettings size={18} />}
            pl={"xs"}
          >
            {lang.senders.linkedin.advanced}
          </Accordion.Control>
          <Accordion.Panel>
            <Stack pt={"md"}>
              <Checkbox
                label={
                  lang.linkedin_accounts.create.dialog
                    .is_sales_navigator_account
                }
                name="is_sales_navigator_account"
                defaultChecked={accountProfile?.is_sales_navigator_account}
                size="md"
              />
              <Checkbox
                label={
                  lang.linkedin_accounts.create.dialog
                    .accept_received_invitations
                }
                name="accept_received_invitations"
                mt={"xs"}
                defaultChecked={accountProfile?.accept_received_invitations}
                size="md"
              />
              <Textarea
                label={
                  lang.linkedin_accounts.create.dialog
                    .accept_received_invitations_message
                }
                maxLength={190}
                defaultValue={
                  accountProfile?.accept_received_invitations_message
                }
                placeholder="ex: Bonjour, ravi d'être connecté avec vous"
                name="accept_received_invitations_message"
                rows={4}
                resize="vertical"
                size="md"
              />
              <NumberInput
                label={
                  lang.linkedin_accounts.create.dialog
                    .max_conversations_messages_extractor
                }
                min={1}
                max={200}
                defaultValue={
                  accountProfile?.max_conversations_messages_extractor || 100
                }
                name="max_conversations_messages_extractor"
                size="md"
              />
              <Divider />
              <Checkbox
                label={
                  lang.linkedin_accounts.create.dialog
                    .withdraw_pending_invitations
                }
                name="withdraw_pending_invitations"
                onChange={(e) => setPurge(e.target.checked)}
                defaultChecked={accountProfile?.withdraw_pending_invitations}
                size="md"
              />
              <Checkbox
                label={
                  lang.linkedin_accounts.create.dialog
                    .withdraw_pending_invitations_oldest
                }
                name="withdraw_pending_invitations_oldest"
                defaultChecked={
                  accountProfile?.withdraw_pending_invitations_oldest
                }
                disabled={!purge}
                size="md"
              />
              <NumberInput
                label={
                  lang.linkedin_accounts.create.dialog
                    .withdraw_pending_invitations_number
                }
                min={1}
                max={1000}
                defaultValue={
                  accountProfile?.withdraw_pending_invitations_number || 100
                }
                name="withdraw_pending_invitations_number"
                disabled={!purge}
                size="md"
              />
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      {isPage && <SharingInAccordion form={sharingForm} />}

      <Group
        justify="end"
        mt={16}
        pos={"sticky"}
        bottom={0}
        bg={"#fff"}
        pt={16}
      >
        {!inList && <BackButton onClick={handleClose} size="md" />}
        <Button
          type="submit"
          leftSection={
            accountProfile ? (
              <IconDeviceFloppy size={18} />
            ) : (
              <IconPlus size={18} />
            )
          }
          color="primary"
          loading={loading}
          size="md"
        >
          {accountProfile
            ? lang.linkedin_accounts.edit.dialog.submit_button
            : lang.linkedin_accounts.create.dialog.submit_button}
        </Button>
      </Group>
      
    </Stack>
  );
};

export default LinkedinAccount;
